import { Plugin, PluginKey, TextSelection } from 'prosemirror-state'
import { getPlaceholderRanges } from './placeholderUtils'

export const placeholderProtectionPluginKey = new PluginKey('placeholderProtection')

/**
 * Keeps the caret out of placeholders and protects their contents.
 *
 * The keydown handlers in Editor.tsx cover keyboard navigation/deletion on
 * desktop, but they can't catch two cases:
 *  - the caret landing inside a placeholder via a mouse click or touch tap
 *  - mobile soft keyboards, which delete via `beforeinput` rather than `keydown`
 *
 * This plugin closes both gaps so the placeholder behaves like an atomic chip
 * regardless of how the user interacts with it.
 */
export const placeholderProtectionPlugin = () =>
  new Plugin({
    key: placeholderProtectionPluginKey,

    // If an empty selection lands inside a placeholder (e.g. a tap on mobile),
    // snap it back out to the nearest edge so the caret never sits within it.
    appendTransaction(_transactions, _oldState, newState) {
      const { selection, doc } = newState
      if (!selection.empty) return null
      const pos = selection.$from.pos
      const range = getPlaceholderRanges(doc).find((r) => pos > r.start && pos < r.end)
      if (!range) return null
      const snapTo = pos - range.start <= range.end - pos ? range.start : range.end
      return newState.tr.setSelection(TextSelection.create(doc, snapTo))
    },

    props: {
      handleDOMEvents: {
        // Mobile soft keyboards (iOS/Android) issue deletions through beforeinput
        // rather than a keydown with `key === 'Backspace'/'Delete'`, so the
        // keydown guards never fire. Intercept the deletion here and remove the
        // whole placeholder instead of letting characters be chipped out of it.
        beforeinput: (view, event) => {
          const inputType = (event as InputEvent).inputType || ''
          if (!inputType.startsWith('delete')) return false

          const { state } = view
          const { selection, doc } = state
          if (!selection.empty) return false

          const pos = selection.$from.pos
          const ranges = getPlaceholderRanges(doc)
          const backward = inputType.toLowerCase().includes('backward')
          const targetRange = backward ? ranges.find((r) => r.end === pos) : ranges.find((r) => r.start === pos)
          if (!targetRange) return false

          event.preventDefault()
          const tr = state.tr.deleteRange(targetRange.start, targetRange.end)
          tr.setSelection(TextSelection.create(tr.doc, targetRange.start))
          view.dispatch(tr)
          return true
        }
      }
    }
  })
